import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import type { EntityRowAction, InventoryEntityType } from '#types/types';
import { useStandardEntityRowActions } from './useStandardEntityRowActions';

interface EntityRowActionButtonProps<T extends { id: string }> {
  action: EntityRowAction<T>;
  entity: T;
  iconOnly: boolean;
}

const EntityRowActionButtonImpl = <T extends { id: string }>({
  action,
  entity,
  iconOnly,
}: EntityRowActionButtonProps<T>) => {
  const isDisabled = action.disabled ? action.disabled(entity) : false;

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (isDisabled) return;
      void action.onClick(entity);
    },
    [action, entity, isDisabled]
  );

  return (
    <button
      type="button"
      title={action.label}
      disabled={isDisabled}
      onClick={handleClick}
      className={clsx(
        'inline-flex h-6 items-center justify-center transition-colors',
        iconOnly
          ? 'w-5 text-white/40 hover:text-white/90'
          : clsx(
              'w-6 rounded-md border',
              action.destructive
                ? 'border-red-400/20 text-red-300/80 hover:border-red-400/40 hover:bg-red-500/10 hover:text-red-200'
                : 'border-white/10 text-white/55 hover:border-white/25 hover:bg-white/10 hover:text-white'
            ),
        isDisabled && (iconOnly
          ? 'cursor-not-allowed opacity-40 hover:text-white/40'
          : 'cursor-not-allowed opacity-40 hover:border-white/10 hover:bg-transparent hover:text-white/55')
      )}
    >
      {action.icon}
    </button>
  );
};

// React.memo perd la généricité du composant : le cast la restaure côté appelant.
const EntityRowActionButton = React.memo(
  EntityRowActionButtonImpl
) as typeof EntityRowActionButtonImpl;

interface EntityRowActionsProps<T extends { id: string }> {
  entityType: InventoryEntityType;
  entity: T;
  rowActions?: EntityRowAction<T>[];
  className?: string;
  /** Render bare icons (no bordered button container), monochrome. */
  iconOnly?: boolean;
}

export const EntityRowActions = <T extends { id: string }>({
  entityType,
  entity,
  rowActions = [],
  className,
  iconOnly = false,
}: EntityRowActionsProps<T>) => {
  const standardActions = useStandardEntityRowActions<T>(entityType);

  const mergedActions = useMemo(() => {
    const actionMap = new Map<string, EntityRowAction<T>>();
    [...standardActions, ...rowActions].forEach((action) => {
      actionMap.set(action.id, action);
    });
    const ordered = Array.from(actionMap.values());
    const focusLikeIds = new Set(['focus', 'focus-link']);
    const trailing = ordered.filter((action) => focusLikeIds.has(action.id));
    if (trailing.length === 0) return ordered;
    const leading = ordered.filter((action) => !focusLikeIds.has(action.id));
    return [...leading, ...trailing];
  }, [rowActions, standardActions]);

  const visibleActions = useMemo(
    () => mergedActions.filter((action) => (action.visible ? action.visible(entity) : true)),
    [entity, mergedActions]
  );

  return (
    <div
      className={clsx(
        'flex items-center opacity-60 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100',
        iconOnly ? 'gap-0' : 'gap-1',
        className
      )}
    >
      {visibleActions.map((action) => (
        <EntityRowActionButton
          key={action.id}
          action={action}
          entity={entity}
          iconOnly={iconOnly}
        />
      ))}
    </div>
  );
};
